import { TWebsiteBuilderRichTextContent } from "../../../richText/websiteBuilderRichTextContent";
import { WebsiteBuilderMigrationError } from "../helpers/migrationError";
import { IWebsiteBuilderMigrationNode, IWebsiteBuilderSchemaVersionMigrationNode } from "../helpers/migrationNode";
import { migrateTraversal } from "../helpers/migrateTraversal";
import { cloneDeep } from "lodash";

interface IMigrateElementV7Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: string;
    settings: Record<string, unknown>;
  }
}

export interface IMigrateHeadingV7Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "heading";
    settings: {
      level?: 1 | 2 | 3 | 4 | 5 | 6;
      text?: string;
      color?: string;
      fontSize?: { desktop?: number; tablet?: number; mobile?: number; };
      lineHeight?: number;
      textAlign?: "left" | "center" | "right";
    };
  }
}

export type TMigrateHeadingV7InputSettings = IMigrateHeadingV7Input["element"]["settings"];

export interface IMigrateParagraphV7Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "paragraph";
    settings: {
      text?: string;
      color?: string;
      fontSize?: { desktop?: number; tablet?: number; mobile?: number; };
      lineHeight?: number;
      textAlign?: "left" | "center" | "right";
    };
  }
}

export type TMigrateParagraphV7InputSettings = IMigrateParagraphV7Input["element"]["settings"];

export interface IMigrateHeadingV8ResultSettings {
  content: TWebsiteBuilderRichTextContent;
}

export interface IMigrateHeadingV8Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "heading";
    settings: IMigrateHeadingV8ResultSettings;
  }
}

export interface IMigrateParagraphV8ResultSettings {
  content: TWebsiteBuilderRichTextContent;
}

export interface IMigrateParagraphV8Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    type: "paragraph";
    settings: IMigrateParagraphV8ResultSettings;
  }
}

function isMigrateElementV7Input(node: any): node is IMigrateElementV7Input {
  const partialNode: Partial<IMigrateElementV7Input> = node;

  return (
    partialNode?.type === "element" &&
    partialNode.element?.type != null &&
    "settings" in partialNode.element
  );
}

function isMigrateHeadingV7Input(node: IMigrateElementV7Input): node is IMigrateHeadingV7Input {
  return node.element.type === "heading";
}

function isMigrateParagraphV7Input(node: IMigrateElementV7Input): node is IMigrateParagraphV7Input {
  return node.element.type === "paragraph";
}

export function migrateSchemaV7ToV8(node: IWebsiteBuilderSchemaVersionMigrationNode): IWebsiteBuilderSchemaVersionMigrationNode {
  if (node.schemaVersion !== 7) {
    throw new WebsiteBuilderMigrationError({
      type: "cannot-migrate-from-schema-version",
      schemaVersion: node.schemaVersion,
      nextSchemaVersion: 8,
    });
  }

  function migrateNode(prev: IWebsiteBuilderMigrationNode): IWebsiteBuilderMigrationNode {
    const throwElementMissingFieldError = () => {
      throw new WebsiteBuilderMigrationError({
        type: "element-missing-field",
        schemaVersion: 7,
        nextSchemaVersion: 8,
      });
    };

    if (prev?.type === "element") {
      if (!isMigrateElementV7Input(prev)) {
        throwElementMissingFieldError();
      } else if (prev.element.type === "heading" || prev.element.type === "paragraph") {
        if (isMigrateHeadingV7Input(prev)) {
          const settings = cloneDeep(prev.element.settings);

          const headingBaseV8Result: IMigrateHeadingV7Input = cloneDeep(prev);

          delete headingBaseV8Result.element.settings;

          const headingV8Result: IMigrateHeadingV8Result = {
            ...headingBaseV8Result,
            element: {
              ...headingBaseV8Result.element,
              settings: {
                content: {
                  type: "doc",
                  attrs: {
                    responsiveFontSize: {
                      desktop: settings.fontSize?.desktop != null ? `${settings.fontSize.desktop}px` : null,
                      tablet: settings.fontSize?.tablet != null ? `${settings.fontSize.tablet}px` : null,
                      mobile: settings.fontSize?.mobile != null ? `${settings.fontSize.mobile}px` : null,
                    },
                  },
                  content: [{
                    type: "heading",
                    attrs: {
                      level: settings.level ?? 1,
                      ...(settings.textAlign != null ? { textAlign: settings.textAlign } : {}),
                      ...(settings.lineHeight != null ? { lineHeight: `${settings.lineHeight}` } : {}),
                    },
                    content: settings.text == null || settings.text === "" ? [] : [{
                      type: "text",
                      text: settings.text,
                      marks: [{
                        type: "textStyle",
                        attrs: {
                          ...(settings.color != null ? { color: settings.color } : {}),
                        },
                      }],
                    }],
                  }],
                },
              },
            },
          };

          return headingV8Result;
        } else if (isMigrateParagraphV7Input(prev)) {
          const settings = cloneDeep(prev.element.settings);

          const paragraphBaseV8Result: IMigrateParagraphV7Input = cloneDeep(prev);

          delete paragraphBaseV8Result.element.settings;

          const paragraphV8Result: IMigrateParagraphV8Result = {
            ...paragraphBaseV8Result,
            element: {
              ...paragraphBaseV8Result.element,
              settings: {
                content: {
                  type: "doc",
                  attrs: {
                    responsiveFontSize: {
                      desktop: settings.fontSize?.desktop != null ? `${settings.fontSize.desktop}px` : null,
                      tablet: settings.fontSize?.tablet != null ? `${settings.fontSize.tablet}px` : null,
                      mobile: settings.fontSize?.mobile != null ? `${settings.fontSize.mobile}px` : null,
                    },
                  },
                  content: [{
                    type: "paragraph",
                    attrs: {
                      ...(settings.textAlign != null ? { textAlign: settings.textAlign } : {}),
                      ...(settings.lineHeight != null ? { lineHeight: `${settings.lineHeight}` } : {}),
                    },
                    content: settings.text == null || settings.text === "" ? [] : [{
                      type: "text",
                      text: settings.text,
                      marks: [{
                        type: "textStyle",
                        attrs: {
                          ...(settings.color != null ? { color: settings.color } : {}),
                        },
                      }],
                    }],
                  }],
                },
              },
            },
          };

          return paragraphV8Result;
        } else {
          throwElementMissingFieldError();
        }
      } else {
        return prev;
      }
    } else {
      return prev;
    }
  }

  return {
    ...migrateTraversal(node, migrateNode),
    schemaVersion: 8,
  };
}